import { ApolloError, isApolloError, useMutation } from '@apollo/client'
import { ForwardToInbox } from '@mui/icons-material'
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material'
import { Dispatch, FC, SetStateAction } from 'react'
import { GetCandidatesForRosterSql } from 'recruiter/contexts'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError } from 'shared/utils'
import { ModalState } from '.'
import { ResendPortalInvitationToRosterCandidates } from './BulkActions.gql'

interface ResendConfirmationEmailProps {
    setModalState: Dispatch<SetStateAction<ModalState>>
    onClose: () => void
    rosterId: string
    candidateIds: string[]
}

const ResendConfirmationEmail: FC<ResendConfirmationEmailProps> = ({
    setModalState,
    rosterId,
    candidateIds,
    onClose,
}) => {
    const toast = useToast()
    const [resendRegistration] = useMutation(
        ResendPortalInvitationToRosterCandidates,
        {
            refetchQueries: [GetCandidatesForRosterSql],
            variables: {
                rosterId,
                candidateIds,
            },
        }
    )

    const handleResendClick = () => {
        setModalState({
            open: true,
            message: `Are you sure you want to email ${candidateIds.length} candidate(s)?`,
            onConfirm: async () => {
                try {
                    const { data } = await resendRegistration()
                    if (data?.resendPortalInvitationEmailBulk.success) {
                        toast({
                            severity: 'success',
                            title: 'Success',
                            message:
                                data.resendPortalInvitationEmailBulk.message ||
                                'Candidates emailed successfully',
                        })
                    } else {
                        toast({
                            severity: 'error',
                            title: 'Error',
                            message:
                                data?.resendPortalInvitationEmailBulk.message ||
                                'Failed to email candidates',
                        })
                    }
                } catch (error) {
                    if (isApolloError(error as Error)) {
                        toast({
                            title: 'Error',
                            message: formatGraphQLError(
                                error as ApolloError,
                                'Something went wrong.'
                            ),
                            severity: 'error',
                        })
                    } else {
                        toast({
                            title: 'Error',
                            message: 'Something went wrong.',
                            severity: 'error',
                        })
                    }
                }
                setModalState({
                    open: false,
                    message: '',
                    onConfirm: () => {},
                })
            },
        })
    }
    return (
        <MenuItem
            onClick={() => {
                handleResendClick()
                onClose()
            }}
            disabled={candidateIds.length === 0}
        >
            <ListItemIcon>
                <ForwardToInbox />
            </ListItemIcon>
            <ListItemText>Send Invite</ListItemText>
        </MenuItem>
    )
}

export default ResendConfirmationEmail
